import { AppSectionKey, IAppSection } from '@/model/app-section.model';
import { DEFAULT_APP_SECTIONS, appSectionKeyByRouteName } from '@/model/app-section.registry';
import { GlobalContext } from '@/model/globalcontext.model';
import store from '@/store';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import axios from 'axios';

const CACHE_KEY = 'cached-app-sections';
const CACHED_AUTHORITIES_KEY = 'cached-authorities';
const PRIVILEGED_AUTHORITIES = ['ROLE_ADMIN', 'ROLE_MANAGER'];

const API = (() => {
  const v = process.env.VUE_APP_API_URL || '';
  return v && !v.endsWith('/') ? v + '/' : v;
})();

function isMobile(): boolean {
  return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
}

function normalizeSections(sections: IAppSection[]): IAppSection[] {
  const byKey = new Map<AppSectionKey, IAppSection>();
  DEFAULT_APP_SECTIONS.forEach(section => byKey.set(section.sectionKey, { ...section }));
  (sections || []).forEach(section => {
    if (section?.sectionKey) {
      byKey.set(section.sectionKey, {
        ...section,
        sectionKey: section.sectionKey.toUpperCase() as AppSectionKey,
        availableToAll: Boolean(section.availableToAll),
      });
    }
  });
  return Array.from(byKey.values()).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
}

class AppSectionService {
  private sections: IAppSection[] = normalizeSections([]);
  private loaded = false;
  private loading?: Promise<IAppSection[]>;
  private cachedAuthorities: string[] = [];
  private globalContext?: GlobalContext;

  public setGlobalContext(globalContext: GlobalContext): void {
    this.globalContext = globalContext;
  }

  public get allSections(): IAppSection[] {
    return this.sections;
  }

  public async loadSections(force = false): Promise<IAppSection[]> {
    if (this.loading && !force) {
      return this.loading;
    }
    this.loading = this.doLoadSections(force);
    try {
      return await this.loading;
    } finally {
      this.loading = undefined;
    }
  }

  public async ensureLoaded(): Promise<IAppSection[]> {
    if (this.loaded) {
      return this.sections;
    }
    return this.loadSections();
  }

  public canAccessRoute(routeName?: string | null): boolean {
    const sectionKey = appSectionKeyByRouteName(routeName);
    return sectionKey ? this.canAccessSection(sectionKey) : true;
  }

  public canAccessSection(sectionKey: AppSectionKey): boolean {
    if (this.hasPrivilegedAccess()) {
      return true;
    }
    const section = this.sections.find(s => s.sectionKey === sectionKey) || DEFAULT_APP_SECTIONS.find(s => s.sectionKey === sectionKey);
    if (!section) {
      return true;
    }
    if (section.availableToAll) {
      return true;
    }
    const roleName = section.roleName?.trim();
    return Boolean(roleName && this.getCurrentAuthorities().includes(roleName));
  }

  public hasPrivilegedAccess(): boolean {
    const authorities = this.getCurrentAuthorities();
    return PRIVILEGED_AUTHORITIES.some(authority => authorities.includes(authority));
  }

  public async clearCache(): Promise<void> {
    this.sections = normalizeSections([]);
    this.cachedAuthorities = [];
    this.loaded = false;
    await Preferences.remove({ key: CACHE_KEY });
    localStorage.removeItem(CACHE_KEY);
  }

  private async doLoadSections(force: boolean): Promise<IAppSection[]> {
    await this.loadCachedAuthorities();

    if (this.globalContext?.offlineMode) {
      await this.loadCachedSections();
      this.loaded = true;
      return this.sections;
    }

    if (!force) {
      await this.loadCachedSections();
    }

    try {
      const sections = await this.fetchSections();
      this.sections = normalizeSections(sections);
      this.loaded = true;
      await this.cacheSections();
      return this.sections;
    } catch (error) {
      console.error('Error loading app sections:', error);
      this.loaded = true;
      return this.sections;
    }
  }

  private async fetchSections(): Promise<IAppSection[]> {
    if (isMobile()) {
      const { value: token } = await Preferences.get({ key: 'jwt' });
      if (!token) {
        return [];
      }
      const res = await CapacitorHttp.get({
        url: `${API}api/app-sections`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IAppSection[];
    }
    const { data } = await axios.get<IAppSection[]>('api/app-sections');
    return data || [];
  }

  private async loadCachedSections(): Promise<void> {
    try {
      const cached = isMobile() ? (await Preferences.get({ key: CACHE_KEY })).value : localStorage.getItem(CACHE_KEY);
      if (cached) {
        this.sections = normalizeSections(JSON.parse(cached));
      }
    } catch (error) {
      console.error('Error reading cached app sections:', error);
    }
  }

  private async cacheSections(): Promise<void> {
    const value = JSON.stringify(this.sections);
    await Preferences.set({ key: CACHE_KEY, value });
    localStorage.setItem(CACHE_KEY, value);
  }

  private async loadCachedAuthorities(): Promise<void> {
    try {
      const cached = isMobile()
        ? (await Preferences.get({ key: CACHED_AUTHORITIES_KEY })).value
        : localStorage.getItem(CACHED_AUTHORITIES_KEY);
      this.cachedAuthorities = cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.error('Error reading cached authorities for app sections:', error);
      this.cachedAuthorities = [];
    }
  }

  private getCurrentAuthorities(): string[] {
    const account = store.getters.account;
    if (account?.authorities && Array.isArray(account.authorities)) {
      return account.authorities;
    }
    return this.cachedAuthorities;
  }
}

export const appSectionService = new AppSectionService();
export default AppSectionService;
